import type {
  AiVerificationCustomChoice,
  AiVerificationQuestion,
  AiVerificationQuestionType,
  AiVerificationRanksTarget,
} from '@citizen-walk/shared-types';
import { fieldOptionsForScope } from './verificationFieldOptions';

/**
 * AI 検証テンプレ質問群 visual editor (Sprint AZ、Phase 5)。
 *
 * 旧 `old/apps/admin/src/features/verification-admin/TemplateEditor*.tsx` の 6 type 別 widget を
 * v2 で 1 component に集約。perPost / perClassification の 2 リスト共通の row 編集 UI と、
 * リスト全体の「+ 追加 / 上下移動 / 削除」操作を提供する。
 *
 * 6 question type:
 *   - three_state (3 値 radio)        — 妥当 / 中立 / 不当
 *   - binary (2 値 radio)             — 妥当 / 不当
 *   - five_scale (5 段階 rating)      — 1〜5
 *   - yes_no (yes/no radio)           — はい / いいえ
 *   - comment (free text)             — 自由記述 (choices 不要)
 *   - custom_choice (任意 options + multiselect 可否) — 選択肢配列の inline 編集
 *
 * 各 row には以下を編集できる:
 *   - type (select)
 *   - label (text、質問文)
 *   - required (checkbox、必須回答?)
 *   - enabled (checkbox、出題するか)
 *   - targetField (text、対象 AI フィールド、null も可)
 *   - helpText (textarea、補足、任意)
 *   - custom_choice の場合: choices[] (label + value)、allowMultiple、allowOther
 *   - perClassification の場合: appliesToRanks (top / top3 / all / number[])、
 *     fallbackHiddenIfMissing
 *
 * 並び替えは drag&drop ではなく ↑ / ↓ button (Sprint AZ minimum scope)。
 *
 * 純 controlled component: `value` (`AiVerificationQuestion[]`) を親が保持、本 component は
 * `onChange(next)` でリストを丸ごと返す (= reducer pattern)。
 */

const TYPE_OPTIONS: { value: AiVerificationQuestionType; label: string }[] = [
  { value: 'three_state', label: '3 値 (妥当 / 中立 / 不当)' },
  { value: 'binary', label: '2 値 (妥当 / 不当)' },
  { value: 'five_scale', label: '5 段階 (1〜5)' },
  { value: 'yes_no', label: 'はい / いいえ' },
  { value: 'comment', label: '自由記述 (comment)' },
  { value: 'custom_choice', label: 'カスタム選択肢 (custom_choice)' },
];

const RANKS_PRESETS: { value: 'top' | 'top3' | 'all' | 'custom'; label: string }[] = [
  { value: 'top', label: '1 位のみ (top)' },
  { value: 'top3', label: '上位 3 位 (top3)' },
  { value: 'all', label: '全 rank (all)' },
  { value: 'custom', label: 'カスタム (number[])' },
];

function makeDefaultQuestion(scope: 'perPost' | 'perClassification'): AiVerificationQuestion {
  // shared-types `createDefault*Question` と同等。重複 ID 防止のため Date.now() + random suffix。
  const suffix = Math.random().toString(36).slice(2, 6);
  if (scope === 'perPost') {
    return {
      id: `q-post-${Date.now()}-${suffix}`,
      enabled: true,
      targetField: null,
      label: '',
      type: 'three_state',
      required: false,
    };
  }
  return {
    id: `q-cls-${Date.now()}-${suffix}`,
    enabled: true,
    targetField: 'category',
    label: '',
    type: 'three_state',
    required: false,
    appliesToRanks: 'top',
    fallbackHiddenIfMissing: true,
  };
}

function ranksPresetOf(
  target: AiVerificationRanksTarget | undefined,
): 'top' | 'top3' | 'all' | 'custom' {
  if (target === 'top' || target === undefined) return 'top';
  if (target === 'top3') return 'top3';
  if (target === 'all') return 'all';
  return 'custom';
}

function parseRanksCustom(text: string): number[] {
  return text
    .split(/[\s,]+/)
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n) && n >= 1);
}

export interface QuestionListEditorProps {
  scope: 'perPost' | 'perClassification';
  value: AiVerificationQuestion[];
  onChange: (next: AiVerificationQuestion[]) => void;
  disabled?: boolean;
}

export function QuestionListEditor({
  scope,
  value,
  onChange,
  disabled = false,
}: QuestionListEditorProps) {
  function update(index: number, patch: Partial<AiVerificationQuestion>) {
    const next = value.slice();
    const current = next[index];
    if (!current) return;
    next[index] = { ...current, ...patch } as AiVerificationQuestion;
    onChange(next);
  }

  function add() {
    onChange([...value, makeDefaultQuestion(scope)]);
  }

  function remove(index: number) {
    const next = value.slice();
    next.splice(index, 1);
    onChange(next);
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= value.length) return;
    const next = value.slice();
    const tmp = next[index]!;
    next[index] = next[target]!;
    next[target] = tmp;
    onChange(next);
  }

  function setType(index: number, type: AiVerificationQuestionType) {
    const current = value[index];
    if (!current) return;
    const patch: Partial<AiVerificationQuestion> = { type };
    // type 切替時の payload sanitize
    if (type === 'custom_choice') {
      if (!current.choices || current.choices.length === 0) {
        patch.choices = [{ label: '選択肢 1' }];
      }
    } else {
      // 他 type に切替時は custom_choice 専用フィールドを除去
      patch.choices = undefined;
      patch.allowMultiple = undefined;
      patch.allowOther = undefined;
    }
    update(index, patch);
  }

  function updateChoice(
    index: number,
    choiceIdx: number,
    patch: Partial<AiVerificationCustomChoice>,
  ) {
    const q = value[index];
    if (!q || !q.choices) return;
    const choices = q.choices.slice();
    choices[choiceIdx] = { ...choices[choiceIdx]!, ...patch };
    update(index, { choices });
  }

  function addChoice(index: number) {
    const q = value[index];
    if (!q) return;
    const choices = (q.choices ?? []).slice();
    choices.push({ label: `選択肢 ${choices.length + 1}` });
    update(index, { choices });
  }

  function removeChoice(index: number, choiceIdx: number) {
    const q = value[index];
    if (!q || !q.choices) return;
    const choices = q.choices.slice();
    choices.splice(choiceIdx, 1);
    update(index, { choices });
  }

  // AW4 (docs/108 High #8) — targetField を datalist 候補化 (PER_POST_FIELDS / PER_CLS_FIELDS)。
  const fieldOptions = fieldOptionsForScope(scope);
  const datalistId = `verification-target-fields-${scope}`;

  return (
    <div className="grid grid-cols-1 gap-3" data-testid={`question-list-${scope}`}>
      <datalist id={datalistId}>
        {fieldOptions
          .filter((o) => o.value !== null)
          .map((o) => (
            <option key={o.value} value={o.value as string}>
              {o.label}
            </option>
          ))}
      </datalist>
      {value.length === 0 && (
        <p className="text-xs text-slate-500 italic">
          質問はまだありません。「+ 質問追加」で追加してください。
        </p>
      )}
      {value.map((q, i) => (
        <div
          key={q.id}
          className="border border-slate-200 rounded p-3 grid grid-cols-1 gap-2 bg-slate-50"
          data-testid={`question-row-${scope}-${i}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-500">
              #{i + 1} · <code>{q.id}</code>
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={disabled || i === 0}
                aria-label="上に移動"
                className="px-2 py-0.5 text-xs border border-slate-300 rounded disabled:opacity-40 bg-white"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={disabled || i === value.length - 1}
                aria-label="下に移動"
                className="px-2 py-0.5 text-xs border border-slate-300 rounded disabled:opacity-40 bg-white"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                aria-label="削除"
                className="px-2 py-0.5 text-xs border border-danger-300 text-danger-700 rounded disabled:opacity-40 bg-white"
              >
                削除
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="grid grid-cols-1 gap-1">
              <span className="text-[11px] text-slate-500">type</span>
              <select
                value={q.type}
                onChange={(e) => setType(i, e.target.value as AiVerificationQuestionType)}
                disabled={disabled}
                aria-label={`質問 ${i + 1} の type`}
                className="min-h-touch px-2 border border-slate-300 rounded text-sm bg-white"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-[11px] text-slate-500">
                対象フィールド (targetField、候補から選択 or 任意入力、空欄=null)
              </span>
              <input
                type="text"
                list={datalistId}
                value={q.targetField ?? ''}
                onChange={(e) =>
                  update(i, { targetField: e.target.value === '' ? null : e.target.value })
                }
                disabled={disabled}
                aria-label={`質問 ${i + 1} の targetField`}
                placeholder={scope === 'perPost' ? '例: description' : '例: category'}
                className="min-h-touch px-2 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>

          <label className="grid grid-cols-1 gap-1">
            <span className="text-[11px] text-slate-500">質問文 (label) *</span>
            <input
              type="text"
              value={q.label}
              onChange={(e) => update(i, { label: e.target.value })}
              disabled={disabled}
              aria-label={`質問 ${i + 1} の label`}
              className="min-h-touch px-2 border border-slate-300 rounded text-sm"
            />
          </label>

          <label className="grid grid-cols-1 gap-1">
            <span className="text-[11px] text-slate-500">補足 (helpText、任意)</span>
            <textarea
              value={q.helpText ?? ''}
              onChange={(e) =>
                update(i, { helpText: e.target.value === '' ? undefined : e.target.value })
              }
              disabled={disabled}
              aria-label={`質問 ${i + 1} の helpText`}
              rows={2}
              className="px-2 py-1 border border-slate-300 rounded text-sm"
            />
          </label>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={q.required}
                onChange={(e) => update(i, { required: e.target.checked })}
                disabled={disabled}
                aria-label={`質問 ${i + 1} の必須`}
              />
              <span>必須回答</span>
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={q.enabled}
                onChange={(e) => update(i, { enabled: e.target.checked })}
                disabled={disabled}
                aria-label={`質問 ${i + 1} を出題する`}
              />
              <span>出題する (enabled)</span>
            </label>
            {scope === 'perClassification' && (
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={q.fallbackHiddenIfMissing ?? false}
                  onChange={(e) =>
                    update(i, {
                      fallbackHiddenIfMissing: e.target.checked ? true : undefined,
                    })
                  }
                  disabled={disabled}
                  aria-label={`質問 ${i + 1} の fallbackHiddenIfMissing`}
                />
                <span>AI 出力に該当 field が無ければ非表示</span>
              </label>
            )}
          </div>

          {/* custom_choice 専用フィールド */}
          {q.type === 'custom_choice' && (
            <div className="border-t border-slate-300 pt-2 grid grid-cols-1 gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-700">選択肢 (choices)</span>
                <button
                  type="button"
                  onClick={() => addChoice(i)}
                  disabled={disabled}
                  className="text-xs text-primary-700 hover:underline"
                >
                  + 選択肢追加
                </button>
              </div>
              {(q.choices ?? []).map((c, ci) => (
                <div key={ci} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={c.label}
                    onChange={(e) => updateChoice(i, ci, { label: e.target.value })}
                    disabled={disabled}
                    aria-label={`質問 ${i + 1} 選択肢 ${ci + 1} の label`}
                    placeholder="label"
                    className="flex-1 min-h-touch px-2 border border-slate-300 rounded text-sm"
                  />
                  <input
                    type="text"
                    value={c.value ?? ''}
                    onChange={(e) =>
                      updateChoice(i, ci, {
                        value: e.target.value === '' ? undefined : e.target.value,
                      })
                    }
                    disabled={disabled}
                    aria-label={`質問 ${i + 1} 選択肢 ${ci + 1} の value`}
                    placeholder="value (省略可)"
                    className="w-32 min-h-touch px-2 border border-slate-300 rounded text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeChoice(i, ci)}
                    disabled={disabled}
                    aria-label={`質問 ${i + 1} 選択肢 ${ci + 1} を削除`}
                    className="px-2 py-0.5 text-xs border border-danger-300 text-danger-700 rounded bg-white"
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-4 mt-1">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={q.allowMultiple ?? false}
                    onChange={(e) =>
                      update(i, { allowMultiple: e.target.checked ? true : undefined })
                    }
                    disabled={disabled}
                    aria-label={`質問 ${i + 1} の allowMultiple`}
                  />
                  <span>複数選択を許可 (allowMultiple)</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={q.allowOther ?? false}
                    onChange={(e) => update(i, { allowOther: e.target.checked ? true : undefined })}
                    disabled={disabled}
                    aria-label={`質問 ${i + 1} の allowOther`}
                  />
                  <span>「その他 (自由記述)」を末尾に追加 (allowOther)</span>
                </label>
              </div>
            </div>
          )}

          {/* perClassification 専用: appliesToRanks */}
          {scope === 'perClassification' && (
            <div className="border-t border-slate-300 pt-2 grid grid-cols-1 gap-2">
              <span className="text-[11px] font-bold text-slate-700">
                適用 rank (appliesToRanks)
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={ranksPresetOf(q.appliesToRanks)}
                  onChange={(e) => {
                    const v = e.target.value as 'top' | 'top3' | 'all' | 'custom';
                    if (v === 'custom') {
                      update(i, { appliesToRanks: [1] });
                    } else {
                      update(i, { appliesToRanks: v });
                    }
                  }}
                  disabled={disabled}
                  aria-label={`質問 ${i + 1} の appliesToRanks`}
                  className="min-h-touch px-2 border border-slate-300 rounded text-sm bg-white"
                >
                  {RANKS_PRESETS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {Array.isArray(q.appliesToRanks) && (
                  <input
                    type="text"
                    value={q.appliesToRanks.join(', ')}
                    onChange={(e) =>
                      update(i, { appliesToRanks: parseRanksCustom(e.target.value) })
                    }
                    disabled={disabled}
                    aria-label={`質問 ${i + 1} の rank 番号 (カンマ区切り)`}
                    placeholder="例: 1, 2, 3"
                    className="flex-1 min-w-[160px] min-h-touch px-2 border border-slate-300 rounded text-sm"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      <div>
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className="text-sm text-primary-700 hover:underline disabled:opacity-40"
          data-testid={`question-add-${scope}`}
        >
          + 質問追加
        </button>
      </div>
    </div>
  );
}

// pure helpers を test から import 可能に re-export
export { makeDefaultQuestion, parseRanksCustom, ranksPresetOf };
